import { cached, tracked } from '@glimmer/tracking';
import { type Registry as Services } from '@ember/service';
import { isPresent } from '@ember/utils';
import { getOwner } from '@ember/application';
import { parseISO } from 'date-fns/parseISO';
import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import type { PropertyRowDataItem } from 'uplisting-frontend/services/calendar/-internals/types';
import UnavailabilityModel from 'uplisting-frontend/models/unavailability';
import BookingModel from 'uplisting-frontend/models/booking';
import EnquiryModel from 'uplisting-frontend/models/enquiry';
import BookingRequestModel from 'uplisting-frontend/models/booking-request';
import { dateUtcAligned, nightsBetween } from 'uplisting-frontend/utils/date';
import type { ConflictRecord } from 'uplisting-frontend/services/calendar/-internals/conflict';

// `from` overrides the record's own start — the drag preview renders the record
// at a hypothetical date, and needs the same ongoing trim when that date has been
// dragged past the left edge of the viewport.
export function computeDurationClass(
  record: OccasionMeta | ConflictRecord,
  from: Date = record.startDate,
): string {
  let { duration } = record;

  const { startDate, dates } = record.calendarManager.viewport;

  const viewportStartAligned = dateUtcAligned(startDate);

  const isOngoing = viewportStartAligned > from;

  if (isOngoing) {
    // calendar days: a dragged `from` is a canonical cell date, hours after
    // local midnight outside UTC, which elapsed days would count a day short
    duration -= differenceInCalendarDays(viewportStartAligned, from);
  }

  const computedDuration = Math.min(duration, dates.length);

  let base = `duration-${computedDuration}`;

  if (isOngoing) {
    base += ' event-ongoing';
  }

  return base;
}

export function occasionStartDate(model: PropertyRowDataItem): Date {
  if (model instanceof BookingModel) {
    return dateUtcAligned(model.persistedStartDate);
  } else if (model instanceof UnavailabilityModel) {
    return parseISO(model.from);
  }

  // dateless enquiries are filtered out before records are built
  // (see calendar/data.ts); the fallback keeps the types honest
  return parseISO(model.checkIn ?? '');
}

export function occasionEndDate(model: PropertyRowDataItem): Date {
  if (model instanceof BookingModel) {
    return dateUtcAligned(model.persistedEndDate);
  } else if (model instanceof UnavailabilityModel) {
    return parseISO(model.to);
  }

  return parseISO(model.checkOut ?? '');
}

export interface IOccasionMeta {
  isDraggable: boolean;
  isResizable: boolean;
  isClickable: boolean;
  cssClasses: string;
}

export class OccasionMeta implements IOccasionMeta {
  currentUser!: ModelRegistry['user'];
  calendarManager!: Services['calendar/manager'];

  @tracked _model!: PropertyRowDataItem;
  @tracked rowNumber!: number;
  @tracked hasOverlap = false;

  constructor(context, model: PropertyRowDataItem) {
    const session = getOwner(context)?.lookup(
      'service:session',
    ) as Services['session'];

    this.currentUser = session.currentUser as ModelRegistry['user'];

    this.calendarManager = getOwner(context)?.lookup(
      'service:calendar/manager',
    ) as Services['calendar/manager'];

    this._model = model;
  }

  @cached
  get isClickable(): boolean {
    if (this._model instanceof BookingModel) {
      return this.currentUser.hasAccessTo('calendar.clickBooking');
    } else if (this._model instanceof EnquiryModel) {
      return this.currentUser.hasAccessTo('calendar.clickEnquiry');
    } else if (this._model instanceof UnavailabilityModel) {
      return this.currentUser.hasAccessTo('calendar.clickUnavailability');
    }

    return true;
  }

  @cached
  get isDraggable(): boolean {
    if (this._model instanceof BookingModel) {
      return (
        this._model.isMoveable &&
        this.currentUser.hasAccessTo('calendar.moveEvent')
      );
    }

    return this.#isMovableBlock;
  }

  @cached
  get isResizable(): boolean {
    if (this._model instanceof BookingModel) {
      return (
        this._model.isEditable &&
        this.currentUser.hasAccessTo('calendar.moveEvent') &&
        this.currentUser.hasAccessTo('bookings.price.edit')
      );
    }

    return this.#isMovableBlock;
  }

  @cached
  get isAirbnb(): boolean {
    if (this._model instanceof UnavailabilityModel) {
      return false;
    }

    return this._model.isAirbnbOfficial;
  }

  @cached
  get isUplisting(): boolean {
    if (this._model instanceof UnavailabilityModel) {
      return false;
    }

    return this._model.isUplisting;
  }

  @cached
  get isVrbo(): boolean {
    if (this._model instanceof UnavailabilityModel) {
      return false;
    }

    return this._model.isVrbo;
  }

  @cached
  get isBookingDotCom(): boolean {
    if (this._model instanceof UnavailabilityModel) {
      return false;
    }

    return this._model.isBookingDotCom;
  }

  @cached
  get duration(): number {
    return nightsBetween(this.startDate, this.endDate);
  }

  @cached
  get startDate(): Date {
    return occasionStartDate(this._model);
  }

  @cached
  get endDate(): Date {
    return occasionEndDate(this._model);
  }

  @cached
  get cssClasses(): string {
    const model = this._model;

    const classes: string[] = [];

    if (this.calendarManager.isOldCalendar) {
      classes.push('timeline-event');
    }

    if (model instanceof BookingModel) {
      classes.push(
        'booking-event',
        `booking-${model.status}`,
        `booking-${model.channel}`,
      );

      if (model.problematic) {
        classes.push('booking-unpaid');
      }
    } else if (model instanceof UnavailabilityModel) {
      const editable = model.isEditable && this.isClickable;

      if (!editable) {
        classes.push('uneditable-unavailability-event');
      }

      classes.push('unavailability-event');
    } else if (model instanceof BookingRequestModel) {
      classes.push(
        'booking-request-event',
        `booking-request-${model.status}`,
        `booking-request-${model.channel}`,
      );
    } else if (model instanceof EnquiryModel) {
      classes.push(
        'enquiry-event',
        `enquiry-${model.status}`,
        `enquiry-${model.channel}`,
      );
    }

    if (this.isClickable) {
      classes.push('cursor-pointer');
    }

    if (isPresent(this.rowNumber) && this.rowNumber !== 0) {
      classes.push('multiline', `multiline-${this.rowNumber + 1}`);
    }

    return classes.join(' ');
  }

  // we need that class only for normal viewport
  // because monthly viewport has its own duration classes
  @cached
  get durationClass(): string {
    return computeDurationClass(this);
  }

  // the API's edit permission, and the panel's, where the move is saved
  get #isMovableBlock(): boolean {
    return (
      this._model instanceof UnavailabilityModel &&
      this._model.isEditable &&
      !this.calendarManager.isOldCalendar &&
      this.currentUser.hasAccessTo('calendar.event.unavailability') &&
      this.currentUser.hasAccessTo('calendar.unavailabilities')
    );
  }
}
